import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@frontegg/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  REPORT,
  CLOUD_TESTS,
  ENVIRONMENTS,
  ENVIRONMENT_INTEGRATIONS,
} from '@elementary/common-ui/constants/queryKeys';
import {
  AccountEnvironment,
  FetchEnvironmentsApi,
} from '@elementary/common-ui/types';
import { useRecoilState, useResetRecoilState } from 'recoil';
import * as atoms from '@elementary/common-ui/atoms/atoms';
import useAccountPermission from '@elementary/common-ui/hooks/useAccountPermission';
import * as api from '../services/settingsApi';

const useEnvironments = ({
  onEnvironmentsLoaded,
}: { onEnvironmentsLoaded?: (envData: FetchEnvironmentsApi) => void } = {}) => {
  const [selectedEnv, setSelectedEnv] = useState<AccountEnvironment>();
  const [isLoaded, setIsLoaded] = useState<boolean>();
  const prevEnvDataRef = useRef<string | null>(null);

  const [selectedEnvId, setSelectedEnvId] = useRecoilState(
    atoms.selectedEnvironmentId,
  );
  const resetSelectedEnvId = useResetRecoilState(atoms.selectedEnvironmentId);

  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { envid: urlEnvId } = useParams();
  const hasAccountPermission = useAccountPermission();

  const switchEnv = useCallback(
    (envId: string, keepSearchParams?: boolean) => {
      const prevEnvId = selectedEnv?.id;

      if (prevEnvId) {
        // Cancel and remove old queries to prevent requests with old envId
        // from firing after the switch
        queryClient.cancelQueries([REPORT, prevEnvId]);
        queryClient.cancelQueries([CLOUD_TESTS, prevEnvId]);
        queryClient.removeQueries([REPORT, prevEnvId]);
        queryClient.removeQueries([CLOUD_TESTS, prevEnvId]);
        queryClient.removeQueries([ENVIRONMENT_INTEGRATIONS, prevEnvId]);
      }

      setSelectedEnvId(envId);

      if (urlEnvId) {
        const curUrl = keepSearchParams ? `${pathname}${search}` : pathname;
        const pathWithNewEnv = curUrl.replace(urlEnvId, envId);
        navigate(pathWithNewEnv, { replace: true });
      }
    },
    [
      navigate,
      pathname,
      queryClient,
      search,
      selectedEnv,
      setSelectedEnvId,
      urlEnvId,
    ],
  );

  const initEnv = useCallback(
    (envData: FetchEnvironmentsApi | undefined, envIdToInit?: string) => {
      if (!envData) return;

      const sortedEnvs = [...envData].sort((a, b) =>
        a.name.localeCompare(b.name),
      );

      const defaultEnv = sortedEnvs[0];

      if (!defaultEnv) {
        resetSelectedEnvId();
        setSelectedEnv(undefined);
        return;
      }

      const envIdToInitData = sortedEnvs?.find((env) => env.id === envIdToInit);

      const urlEnvIdData = sortedEnvs?.find((env) => env.id === urlEnvId);

      const selectedEnvData = sortedEnvs?.find(
        (env) => env.id === selectedEnvId,
      );

      const envToSet = envIdToInitData || urlEnvIdData || selectedEnvData;

      if (envToSet) {
        setSelectedEnv(envToSet);
        if (selectedEnvId !== envToSet.id || selectedEnvId !== urlEnvId) {
          switchEnv(envToSet?.id);
        }
      } else {
        setSelectedEnv(defaultEnv);
        if (selectedEnvId !== defaultEnv.id || selectedEnvId !== urlEnvId) {
          switchEnv(defaultEnv.id);
        }
      }
    },
    [urlEnvId, selectedEnvId, resetSelectedEnvId, switchEnv],
  );

  const { data, isLoading, isFetched, isError } = useQuery(
    [ENVIRONMENTS],
    api.fetchEnvironments,
    {
      enabled: isAuthenticated,
      onSuccess: (envData) => {
        const envDataStr = JSON.stringify(envData);
        if (prevEnvDataRef.current !== envDataStr) {
          prevEnvDataRef.current = envDataStr;
          initEnv(envData);
          onEnvironmentsLoaded?.(envData);
        }
      },
    },
  );

  useEffect(() => {
    initEnv(data);
    setIsLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEnvId]);

  const sortedData = useMemo(() => {
    if (!data) return [];
    return [...data].sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  const hasNoEnvironmentAccess = useMemo(
    () =>
      isFetched && !data?.[0] && !hasAccountPermission('CREATE_ENVIRONMENTS'),
    [isFetched, data, hasAccountPermission],
  );

  return {
    data: sortedData,
    isLoaded,
    isLoading: !isLoaded || isLoading,
    isFetched,
    isError,
    selectedEnv: isFetched ? selectedEnv : undefined,
    selectedEnvId: isFetched ? selectedEnvId : undefined,
    setSelectedEnv: (envId?: string) => initEnv(data, envId),
    hasNoEnvironmentAccess,
  };
};

export default useEnvironments;
